// Shared by the build pull and the browser refresh. Keep this module browser-safe.
// Vue messages retain Vue's own grammar, including rich text, plurals and links.
import { baseCompile } from '@intlify/message-compiler';

const SUPPORTED_LOCALES = new Set(['en', 'ar']);
const SUPPORTED_STAGES = new Set(['dev', 'production']);
const UNSAFE_NAMES = new Set(['__proto__', 'prototype', 'constructor']);

function isRecord(value) {
   return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function validateTranslationTarget(api, stage) {
   if (!SUPPORTED_STAGES.has(stage)) throw new Error('translations: invalid stage');
   const target = new URL(api);
   const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(target.hostname);
   if (
      target.username ||
      target.password ||
      target.search ||
      target.hash ||
      (target.protocol !== 'https:' && !(target.protocol === 'http:' && loopback))
   ) {
      throw new Error('translations: invalid API URL');
   }
   if (
      (target.hostname === 'api-dev.talktime.ae' && stage !== 'dev') ||
      (target.hostname === 'api.talktime.ae' && stage !== 'production')
   ) {
      throw new Error('translations: API environment and stage disagree');
   }
   return target.toString().replace(/\/$/, '');
}

export function flattenMessages(messages) {
   if (!isRecord(messages)) throw new Error('translations: messages must be an object');
   const flat = new Map();
   function visit(node, prefix, depth) {
      if (depth > 32) throw new Error('translations: message nesting exceeds 32 levels');
      const entries = Object.entries(node);
      if (entries.length === 0) throw new Error(`translations: empty message group ${prefix}`);
      for (const [name, value] of entries) {
         if (!name || name.includes('.') || UNSAFE_NAMES.has(name)) {
            throw new Error(`translations: invalid message segment ${name}`);
         }
         const key = prefix ? `${prefix}.${name}` : name;
         if (typeof value === 'string' && value.trim()) flat.set(key, value);
         else if (isRecord(value)) visit(value, key, depth + 1);
         else throw new Error(`translations: ${key} must be a nonempty string or message group`);
      }
   }
   visit(messages, '', 0);
   return flat;
}

// Texts that name the product link one shared key instead of repeating the
// name. Such a link substitutes display copy only: it adds no parameter and no
// plural branch, so adding or removing it is a wording change, not a contract
// change. The link must still resolve (see vueMessageLinkedKeys).
const APP_KEY_PREFIX = 'app.';

export const SUBSTITUTION_KEYS = new Set(['brand.name']);

function compileBranches(message) {
   const { ast } = baseCompile(message, {
      jit: true,
      optimize: false,
      onError(error) {
         throw new Error(`translations: invalid Vue message: ${error.message}`);
      },
   });
   return ast.body.type === 1 ? ast.body.cases : [ast.body];
}

function linkDependency(key) {
   return key.type === 4
      ? `named:${key.key}`
      : key.type === 5
        ? `list:${key.index}`
        : `key:${key.value}`;
}

// Copy may change, but code still supplies the same named/list parameters and
// chooses the same plural branches. Linked messages are dependencies too,
// except links to a substitution key.
export function vueMessageSignature(message) {
   return compileBranches(message).map((branch) => {
      const parameters = new Set();
      const links = new Set();
      for (const item of branch.items) {
         if (item.type === 4) parameters.add(`named:${item.key}`);
         if (item.type === 5) parameters.add(`list:${item.index}`);
         if (item.type === 6) {
            const key = item.key;
            const dependency = linkDependency(key);
            if (key.type === 4 || key.type === 5) parameters.add(dependency);
            else if (SUBSTITUTION_KEYS.has(key.value)) continue;
            links.add(`${item.modifier?.value ?? ''}:${dependency}`);
         }
      }
      return { parameters: [...parameters].sort(), links: [...links].sort() };
   });
}

// Every literal key a message links to, substitution keys included: the
// signature leaves those out, but a link to a missing key still renders nothing.
export function vueMessageLinkedKeys(message) {
   const keys = new Set();
   for (const branch of compileBranches(message)) {
      for (const item of branch.items) {
         if (item.type === 6 && item.key.type !== 4 && item.key.type !== 5) {
            keys.add(item.key.value);
         }
      }
   }
   return [...keys].sort();
}

// Bootstrap only the two exact defects in the historical Arabic seed. This
// builds a validation contract, never rendered copy. Once corrected API bundles
// generate fresh seeds, neither exception matches and this is a no-op.
export function prepareTranslationCompatibilityBaseline(locale, seed, englishSeed) {
   if (!SUPPORTED_LOCALES.has(locale)) throw new Error('translations: unsupported baseline locale');
   let baseline = seed;
   if (locale === 'ar') {
      const defects = [
         { group: 'client', key: 'therapist_already_selected', oldValue: '' },
         { group: 'sessions', key: 'therapistPicture', oldValue: '{الإسم} Picture' },
      ];
      for (const defect of defects) {
         if (
            !isRecord(baseline?.[defect.group]) ||
            baseline[defect.group][defect.key] !== defect.oldValue
         )
            continue;
         const reference = englishSeed?.[defect.group]?.[defect.key];
         if (typeof reference !== 'string' || !reference.trim()) {
            throw new Error(
               `translations: missing English bootstrap contract for ${defect.group}.${defect.key}`,
            );
         }
         // Validate the reference before trusting its parameters/plural grammar.
         vueMessageSignature(reference);
         baseline = {
            ...baseline,
            [defect.group]: { ...baseline[defect.group], [defect.key]: reference },
         };
      }
   }
   for (const [key, message] of flattenMessages(baseline)) {
      try {
         vueMessageSignature(message);
      } catch (cause) {
         throw new Error(`${locale} baseline/${key}: ${cause.message}`);
      }
   }
   return baseline;
}

export function validateTranslationBundle(
   body,
   { locale, stage, environment, requiredKeys = [], baseline } = {},
) {
   if (!SUPPORTED_LOCALES.has(locale) || !SUPPORTED_STAGES.has(stage)) {
      throw new Error('translations: unsupported locale or stage');
   }
   if (
      !isRecord(body) ||
      body.locale !== locale ||
      body.stage !== stage ||
      !Number.isSafeInteger(body.version) ||
      body.version < 0
   ) {
      throw new Error(`translations: invalid ${locale}/${stage} bundle metadata`);
   }
   // Additive metadata is optional during rollout to existing API instances.
   if (body.schema_version !== undefined && body.schema_version !== 1) {
      throw new Error('translations: incompatible message schema');
   }
   if (
      environment !== undefined &&
      body.environment !== undefined &&
      body.environment !== environment
   ) {
      throw new Error('translations: response belongs to another environment');
   }
   const flat = flattenMessages(body.messages);
   for (const key of requiredKeys) {
      if (!flat.has(key)) throw new Error(`translations: missing required ${locale} key ${key}`);
   }
   const signatures = new Map();
   const linkedKeys = new Map();
   for (const [key, message] of flat) {
      try {
         signatures.set(key, JSON.stringify(vueMessageSignature(message)));
         linkedKeys.set(key, vueMessageLinkedKeys(message));
      } catch (cause) {
         throw new Error(`${locale}/${key}: ${cause.message}`);
      }
   }
   if (baseline) {
      for (const [key, previous] of flattenMessages(baseline)) {
         // The hub shows no app text; those keys belong to the mobile app, whose
         // own validator guards them.
         if (key.startsWith(APP_KEY_PREFIX)) continue;
         if (!flat.has(key)) throw new Error(`translations: missing seeded ${locale} key ${key}`);
         if (signatures.get(key) !== JSON.stringify(vueMessageSignature(previous))) {
            throw new Error(
               `translations: incompatible Vue parameters/plurals/links for ${locale}/${key}`,
            );
         }
      }
   }
   // Literal links must resolve in the complete bundle. Dynamic links are
   // validated structurally above; their value is selected by consumer code.
   for (const [key, linked] of linkedKeys) {
      for (const target of linked) {
         if (!flat.has(target)) {
            throw new Error(`translations: ${locale}/${key} links to a missing key`);
         }
      }
   }
   return body;
}

export function validateTranslationPair(bundles) {
   if (
      bundles.length !== 2 ||
      bundles[0].locale !== 'en' ||
      bundles[1].locale !== 'ar' ||
      bundles[0].stage !== bundles[1].stage ||
      bundles[0].version !== bundles[1].version ||
      bundles[0].schema_version !== bundles[1].schema_version ||
      bundles[0].environment !== bundles[1].environment
   ) {
      throw new Error('translations: English and Arabic bundle revisions disagree');
   }
   return bundles;
}

export async function fetchTranslationPair(
   fetchLocale,
   { stage, environment, requiredKeys = [], baselines, attempts = 3 } = {},
) {
   const compatibilityBaselines =
      baselines &&
      Object.fromEntries(
         Object.entries(baselines).map(([locale, seed]) => [
            locale,
            prepareTranslationCompatibilityBaseline(locale, seed, baselines.en),
         ]),
      );
   for (let attempt = 0; attempt < attempts; attempt++) {
      const bundles = await Promise.all(
         ['en', 'ar'].map(async (locale) =>
            validateTranslationBundle(await fetchLocale(locale, attempt), {
               locale,
               stage,
               environment,
               requiredKeys,
               // Preserve dynamic namespaces and other seeded dependencies too.
               // Seed retirement requires a supported-consumer manifest first.
               baseline: compatibilityBaselines?.[locale],
            }),
         ),
      );
      try {
         return validateTranslationPair(bundles);
      } catch (error) {
         if (attempt === attempts - 1) throw error;
      }
   }
   throw new Error('translations: no coherent bundle pair');
}

// State belongs to one configured API/stage/schema and is never shared across
// environments. Failed, incompatible or superseded requests preserve the last
// applied messages, initially the build seed.
export function createTranslationRefresher({
   api,
   stage,
   environment,
   seeds,
   getLocale,
   applyMessages,
   fetcher = fetch,
}) {
   const target = validateTranslationTarget(api, stage);
   const compatibilityBaselines = Object.fromEntries(
      Object.entries(seeds).map(([locale, seed]) => [
         locale,
         prepareTranslationCompatibilityBaseline(locale, seed, seeds.en),
      ]),
   );
   const versions = new Map();
   let request = 0;
   return async function refresh() {
      const locale = getLocale();
      const currentRequest = ++request;
      if (!SUPPORTED_LOCALES.has(locale) || !seeds[locale]) return false;
      try {
         const response = await fetcher(`${target}/content/translations/${stage}/${locale}`);
         if (!response.ok) return false;
         const body = validateTranslationBundle(await response.json(), {
            locale,
            stage,
            environment,
            baseline: compatibilityBaselines[locale],
         });
         if (getLocale() !== locale || request !== currentRequest) return false;
         const cacheKey = `${target}|${stage}|1|${locale}`;
         const previousVersion = versions.get(cacheKey);
         if (previousVersion !== undefined && body.version < previousVersion) return false;
         applyMessages(locale, body.messages);
         versions.set(cacheKey, body.version);
         return true;
      } catch {
         return false;
      }
   };
}
